import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ApiError } from '@awj/api-client';
import { formatDate, formatDateTime } from '@awj/i18n';
import { Button, Skeleton, useToast } from '@awj/ui';

import { Icon } from '~/components/Icon.tsx';
import { PageTitle } from '~/components/PageTitle.tsx';
import { Pager } from '~/components/Pager.tsx';
import { Select } from '~/components/Select.tsx';
import { useI18n } from '~/i18n/index.tsx';
import {
  useAddInquiryNote,
  useInquiries,
  useInquiry,
  useSetInquiryStatus,
} from '~/lib/inbox-queries.ts';

const STATUSES = ['New', 'InProgress', 'Resolved', 'Spam'] as const;

/**
 * What can be done from each status. Every other status used to be offered, so a resolved
 * enquiry showed "Pick up" beside "Reopen" — two ways of saying the same thing, one of them wrong.
 */
const NEXT: Record<(typeof STATUSES)[number], readonly (typeof STATUSES)[number][]> = {
  New: ['InProgress', 'Resolved', 'Spam'],
  InProgress: ['Resolved', 'Spam'],
  // Spam can still be recognised after the fact.
  Resolved: ['New', 'Spam'],
  Spam: ['New'],
};
const TYPES = ['Contact', 'Business'] as const;

/**
 * The enquiry inbox: a list on one side, the conversation on the other.
 *
 * Two panes rather than a list and a separate page, because working an inbox means reading one,
 * dealing with it, and moving to the next — and a round trip to a detail page and back turns a
 * ten-message morning into twenty navigations.
 *
 * Which enquiry is open lives in the URL, so a colleague can be sent a link to the one being
 * discussed.
 */
export function InquiriesPage() {
  const { t, locale } = useI18n();
  const toast = useToast();
  const [params, setParams] = useSearchParams();

  const query = {
    search: params.get('q') ?? '',
    status: params.get('status') ?? '',
    type: params.get('type') ?? '',
    page: Number(params.get('page')) || 1,
  };

  const openId = params.get('id') ? Number(params.get('id')) : null;

  const inquiries = useInquiries(query);
  const inquiry = useInquiry(openId);
  const setStatus = useSetInquiryStatus();
  const addNote = useAddInquiryNote();

  const [search, setSearch] = useState(query.search);
  const [note, setNote] = useState('');

  useEffect(() => setSearch(query.search), [query.search]);
  // A note half-typed against one enquiry must not follow you to the next.
  useEffect(() => setNote(''), [openId]);

  /*
   * On a narrow page the message sits under the list, so opening the eighth enquiry would open it
   * off the bottom of the screen. Bring it up — but only when the panes are stacked: beside the
   * list it is already in view, and scrolling the page there would only jolt it.
   */
  const list = useRef<HTMLDivElement>(null);
  const detail = useRef<HTMLDivElement>(null);
  // Once the message has loaded, not when it is chosen: an empty pane at the foot of the page can
  // only be scrolled to the bottom of the screen, and the message then grows down out of view.
  const shownId = inquiry.data?.id;

  useEffect(() => {
    if (shownId === undefined || !list.current || !detail.current) {
      return;
    }

    if (detail.current.getBoundingClientRect().top >= list.current.getBoundingClientRect().bottom) {
      detail.current.scrollIntoView({ block: 'start' });
    }
  }, [shownId]);

  useEffect(() => {
    if (search === query.search) {
      return;
    }

    const timer = setTimeout(() => update({ q: search }), 350);

    return () => clearTimeout(timer);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  function update(changes: Record<string, string>) {
    const next = new URLSearchParams(params);

    for (const [key, value] of Object.entries(changes)) {
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
    }

    if (!('page' in changes)) {
      next.delete('page');
    }

    setParams(next, { replace: true });
  }

  const open = inquiry.data;

  return (
    <>
      <PageTitle title={t('nav.inquiries')} />

      <div className="filter-bar">
        <div className="filter-bar__row">
          <label className="filter-bar__search">
            <span className="sr-only">{t('inquiries.search')}</span>
            <span className="filter-bar__search-icon">
              <Icon name="search" size={16} />
            </span>
            <input
              className="filter-bar__search-input"
              type="search"
              value={search}
              placeholder={t('inquiries.search')}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>

          <div className="filter-bar__controls">
            <div className="filter-bar__field">
              <label htmlFor="filter-status">{t('inquiries.status')}</label>
              <Select
                id="filter-status"
                value={query.status}
                onChange={(status) => update({ status })}
                placeholder={t('inquiries.allStatuses')}
                options={STATUSES.map((status) => ({
                  value: status,
                  label: t(`inquiryStatus.${status}` as 'inquiryStatus.New'),
                }))}
              />
            </div>

            <div className="filter-bar__field">
              <label htmlFor="filter-type">{t('inquiries.type')}</label>
              <Select
                id="filter-type"
                value={query.type}
                onChange={(type) => update({ type })}
                placeholder={t('inquiries.allTypes')}
                options={TYPES.map((type) => ({
                  value: type,
                  label: t(`inquiryType.${type}` as 'inquiryType.Contact'),
                }))}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="inbox-split">
        <div className="inbox-split__list" ref={list}>
          {inquiries.isPending ? (
            <Skeleton height="320px" />
          ) : inquiries.isError ? (
            <div className="empty-panel">
              <p>{t('common.somethingWentWrong')}</p>
            </div>
          ) : inquiries.data.items.length === 0 ? (
            <div className="empty-panel">
              <p>{t('inquiries.none')}</p>
            </div>
          ) : (
            <>
              <ul className="thread-list">
                {inquiries.data.items.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={`thread-list__item ${openId === item.id ? 'is-open' : ''}`}
                      aria-current={openId === item.id ? 'true' : undefined}
                      onClick={() => update({ id: String(item.id), page: String(query.page) })}
                    >
                      <span className="thread-list__top">
                        <span className="thread-list__from">
                          <bdi>{item.name}</bdi>
                        </span>
                        <span className={`status status--inquiry-${item.status.toLowerCase()}`}>
                          {t(`inquiryStatus.${item.status}` as 'inquiryStatus.New')}
                        </span>
                      </span>
                      <span className="thread-list__subject">
                        <bdi>{item.subject || t(`inquiryType.${item.type}` as 'inquiryType.Contact')}</bdi>
                      </span>
                      <span className="thread-list__preview">
                        <bdi>{item.preview}</bdi>
                      </span>
                      <span className="thread-list__foot">
                        <bdi>{formatDate(locale, item.createdAt)}</bdi>
                        {item.companyName ? (
                          <>
                            {' · '}
                            <bdi>{item.companyName}</bdi>
                          </>
                        ) : null}
                        {item.noteCount > 0 ? (
                          <>
                            {' · '}
                            {t('inquiries.noteCount', { count: String(item.noteCount) })}
                          </>
                        ) : null}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>

              <Pager
                page={inquiries.data.page}
                totalPages={inquiries.data.totalPages ?? 1}
                total={inquiries.data.total}
                onPage={(next) => update({ page: String(next) })}
              />
            </>
          )}
        </div>

        <div
          ref={detail}
          className={`inbox-split__detail ${openId === null ? 'inbox-split__detail--empty' : ''}`}
        >
          {openId === null ? (
            <div className="empty-panel empty-panel--tall">
              <span className="empty-panel__icon">
                <Icon name="inbox" size={26} />
              </span>
              <p className="empty-panel__title">{t('inquiries.pickOne')}</p>
              <p>{t('inquiries.pickOneHint')}</p>
            </div>
          ) : inquiry.isPending ? (
            <Skeleton height="420px" />
          ) : inquiry.isError || !open ? (
            <div className="empty-panel">
              <p>{t('inquiries.notFound')}</p>
            </div>
          ) : (
            <article className="card" aria-labelledby="inquiry-title">
              <header className="inquiry__head">
                <div>
                  <h2 className="inquiry__subject" id="inquiry-title">
                    <bdi>{open.subject || t(`inquiryType.${open.type}` as 'inquiryType.Contact')}</bdi>
                  </h2>
                  <p className="inbox__meta">
                    <bdi>{open.name}</bdi>
                    {' · '}
                    <a href={`mailto:${open.email}`}>
                      <bdi>{open.email}</bdi>
                    </a>
                    {open.phoneNumber ? (
                      <>
                        {' · '}
                        <a href={`tel:${open.phoneNumber}`} dir="ltr">
                          {open.phoneNumber}
                        </a>
                      </>
                    ) : null}
                  </p>
                  <p className="inbox__meta">
                    <bdi>{formatDateTime(locale, open.createdAt)}</bdi>
                    {' · '}
                    {t(`inquiryType.${open.type}` as 'inquiryType.Contact')}
                    {open.assignedToEmail ? (
                      <>
                        {' · '}
                        {t('inquiries.pickedUpBy', { who: open.assignedToEmail })}
                      </>
                    ) : null}
                  </p>
                </div>

                <span className={`status status--inquiry-${open.status.toLowerCase()}`}>
                  {t(`inquiryStatus.${open.status}` as 'inquiryStatus.New')}
                </span>
              </header>

              {/*
                A trade enquiry carries three fields a contact-form message does not, and they are
                the whole point of it — quoting for 200 boxes a month is a different conversation.
              */}
              {open.type === 'Business' ? (
                <dl className="trade-facts">
                  {open.companyName ? (
                    <div>
                      <dt>{t('inquiries.company')}</dt>
                      <dd>
                        <bdi>{open.companyName}</bdi>
                      </dd>
                    </div>
                  ) : null}
                  {open.estimatedQuantity ? (
                    <div>
                      <dt>{t('inquiries.quantity')}</dt>
                      <dd>
                        <bdi>{open.estimatedQuantity}</bdi>
                      </dd>
                    </div>
                  ) : null}
                  {open.frequency ? (
                    <div>
                      <dt>{t('inquiries.frequency')}</dt>
                      <dd>
                        <bdi>{open.frequency}</bdi>
                      </dd>
                    </div>
                  ) : null}
                </dl>
              ) : null}

              <p className="inquiry__message">
                <bdi>{open.message}</bdi>
              </p>

              <div className="inquiry__actions">
                {(NEXT[open.status as (typeof STATUSES)[number]] ?? STATUSES).map((status) => (
                  <Button
                    key={status}
                    type="button"
                    variant={status === 'Spam' ? 'ghost' : 'secondary'}
                    size="sm"
                    loading={setStatus.isPending}
                    onClick={async () => {
                      try {
                        await setStatus.mutateAsync({ id: open.id, status });
                        toast.show({
                          title: t('inquiries.movedTo', {
                            status: t(`inquiryStatus.${status}` as 'inquiryStatus.New'),
                          }),
                          tone: 'success',
                        });
                      } catch (error) {
                        toast.show({
                          title: t('inquiries.couldNotMove'),
                          message: error instanceof ApiError ? error.problem.detail : undefined,
                          tone: 'danger',
                        });
                      }
                    }}
                  >
                    {t(`inquiryAction.${status}` as 'inquiryAction.New')}
                  </Button>
                ))}

                <a className="awj-button awj-button--primary awj-button--sm" href={`mailto:${open.email}`}>
                  {t('inquiries.emailBack')}
                </a>
              </div>

              <section className="inquiry__notes" aria-labelledby="notes-title">
                <h3 className="section-label" id="notes-title">
                  {t('inquiries.notes')}
                </h3>

                {open.notes.length === 0 ? (
                  <p className="field__hint">{t('inquiries.noNotes')}</p>
                ) : (
                  <ol className="note-list">
                    {open.notes.map((entry) => (
                      <li key={entry.id}>
                        <p className="note-list__body">
                          <bdi>{entry.body}</bdi>
                        </p>
                        <p className="note-list__meta">
                          <bdi>{entry.authorEmail ?? t('inquiries.someone')}</bdi>
                          {' · '}
                          <bdi>{formatDateTime(locale, entry.createdAt)}</bdi>
                        </p>
                      </li>
                    ))}
                  </ol>
                )}

                <form
                  className="note-form"
                  onSubmit={async (event) => {
                    event.preventDefault();

                    if (!note.trim()) {
                      return;
                    }

                    try {
                      await addNote.mutateAsync({ id: open.id, body: note.trim() });
                      setNote('');
                    } catch (error) {
                      toast.show({
                        title: t('inquiries.couldNotAddNote'),
                        message: error instanceof ApiError ? error.problem.detail : undefined,
                        tone: 'danger',
                      });
                    }
                  }}
                >
                  <label className="field">
                    <span className="sr-only">{t('inquiries.addNote')}</span>
                    <textarea
                      className="awj-textarea"
                      rows={2}
                      value={note}
                      placeholder={t('inquiries.addNote')}
                      onChange={(event) => setNote(event.target.value)}
                    />
                  </label>
                  <Button type="submit" size="sm" loading={addNote.isPending} disabled={!note.trim()}>
                    {t('inquiries.addNote')}
                  </Button>
                </form>
              </section>
            </article>
          )}
        </div>
      </div>
    </>
  );
}
